import styled from "@emotion/styled";
import { Navbar } from "../../features/Navbar";
import { Box, Typography } from "@mui/material";
import {
  PageBreadcrumbs,
  PageBreadcrumbsProps,
} from "../components/PageBreadcrumbs";
import { RootLayout } from "./RootLayout";
import { Helmet } from "react-helmet-async";
import { forwardRef } from "react";
import { HelpButton } from "@shared/components/HelpButton";
import { useAppName } from "@shared/hooks/useAppName";
import { useAppDescription } from "@shared/hooks/useAppDescription";

const PageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 4rem 1fr;
  min-height: 100vh;
  @supports (height: 100dvh) {
    min-height: 100dvh;
  }
  background-color: ${(p) => p.theme.colors.background2};

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
    grid-template-rows: 4rem 1fr;
  }

  &[aria-disabled="true"] {
    pointer-events: none;
    * {
      pointer-events: none;
    }
  }
`;

const Container = styled.div`
  padding: 3rem 4rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow: auto;

  h1 {
    margin: 0;
    margin-bottom: 1rem;
    color: ${(p) => p.theme.text.colors.primary};
  }

  ${(p) => p.theme.media.mobileDown} {
    padding: 2rem 1rem;
  }

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const Footer = styled.div`
  margin: 3rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  left: 50%;
  transform: translateX(-50%);

  a {
    margin: 0;
    color: ${(p) => p.theme.text.colors.secondary};
  }
`;

const TopActionsContainer = styled(Box)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
`;

interface PageLayoutProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  children: React.ReactNode;
  showFooter?: boolean;
  breadcrumbs?: PageBreadcrumbsProps;
  theme?: "dark" | "light";
  disabled?: boolean;
  topActions?: React.ReactNode;
}

export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  (props, ref) => {
    const {
      children,
      breadcrumbs,
      showFooter = false,
      theme,
      disabled,
      topActions,
      ...rest
    } = props;
    const appName = useAppName();
    const appDescription = useAppDescription();

    const content = (
      <PageContainer {...rest} aria-disabled={disabled} ref={ref}>
        <Helmet>
          <title>{appName}</title>
          <meta name="description" content={appDescription} />
        </Helmet>
        <Navbar />
        <Container>
          {(!!breadcrumbs || !!topActions) && (
            <TopActionsContainer mb={topActions ? 2 : 0}>
              {!!breadcrumbs && <PageBreadcrumbs {...breadcrumbs} />}
              {!!topActions && topActions}
            </TopActionsContainer>
          )}
          {children}
        </Container>
        {showFooter && (
          <Footer>
            <Typography>
              <a href="mailto:support@sidekickdata.io">
                support@sidekickdata.io
              </a>
            </Typography>
          </Footer>
        )}
      </PageContainer>
    );

    return (
      <RootLayout theme={theme}>
        {content}
        <HelpButton mode="floating" />
      </RootLayout>
    );
  },
);

PageLayout.displayName = "PageLayout";
